import { forwardRef, Fragment, ReactElement, ReactNode } from "react";
import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  LinkButton,
  LinkButtonProps,
} from "./Button";
import { IconProps } from "./Icon";
import { Menu } from "@headlessui/react";
import { FloatingOverlay } from "./FloatingOverlay";
import { classNames } from "../utils/classNames";
import { renderIcon } from "./iconHelpers";
import { IconName } from "./iconMapping";
import { FloatingDropdownParams, useFloatingDropdown } from "./floatingHelpers";

import { useDispatch, useMouseEventHandler, useTranslation } from "../stores/RootStoreContext";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "../i18n/tt";
import { isFunction } from "lodash";
import { DCEvent } from "../events/eventsDomain";

export const DropdownLinkButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean } & Omit<LinkButtonProps, "type" | "arrow">
>(({ isOpen, onClick, ...props }, ref) => {
  const clickHandler = useMouseEventHandler(onClick);
  return (
    <LinkButton
      ref={ref}
      {...props}
      arrow={!props.icon && (isOpen ? "up" : "down")}
      onClick={(event) => {
        event.stopPropagation();
        clickHandler?.(event);
      }}
    />
  );
});

export const DropdownIconButton = forwardRef<
  HTMLButtonElement,
  { tooltip?: string | TMessage } & Omit<IconButtonProps, "type" | "tooltip">
>(({ tooltip, onClick, ...props }, ref) => {
  const { t } = useTranslation();
  const clickHandler = useMouseEventHandler(onClick);
  return (
    <IconButton
      ref={ref}
      {...props}
      tooltip={isTMessage(tooltip) ? t(tooltip) : (tooltip ?? t("open-menu"))}
      onClick={(event) => {
        event.stopPropagation();
        clickHandler?.(event);
      }}
    />
  );
});

export const DropdownButton = forwardRef<
  HTMLButtonElement,
  { isOpen: boolean; disableArrow?: boolean } & Omit<ButtonProps, "type" | "arrow">
>(({ isOpen, onClick, disableArrow, ...props }, ref) => {
  const clickHandler = useMouseEventHandler(onClick);
  return (
    <Button
      ref={ref}
      {...props}
      arrow={props.label && !disableArrow ? (isOpen ? "up" : "down") : undefined}
      onClick={(event) => {
        event.stopPropagation();
        clickHandler?.(event);
      }}
    />
  );
});

const placeholderIcon: IconProps = { name: "info", className: "tw-invisible" };

function renderMenuItemIcon({
  icon,
  menuHasIcons,
}: {
  icon?: IconName | IconProps;
  menuHasIcons: boolean;
}) {
  return icon ? renderIcon(icon) : menuHasIcons ? renderIcon(placeholderIcon) : undefined;
}

export type DropdownMenuItemProps = {
  type: "item";
  label: TMessage | TMessageParam | ReactNode;
  categoryTag?: TMessage | TMessageParam | ReactNode;
  icon?: IconName | IconProps;
  id?: string;
  color?: "default" | "error";
  onSelect: (() => void) | DCEvent[];
  isDisabled?: boolean;
  tooltip?: TMessage | TMessageParam | string;
};

function DropdownMenuItem({
  label,
  color = "default",
  onSelect,
  isDisabled,
  tooltip,
  ...props
}: { menuHasIcons: boolean } & Omit<DropdownMenuItemProps, "type">) {
  const { tf } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Menu.Item as={Fragment} disabled={isDisabled}>
      {({ active, close }) => (
        <div
          className={classNames(
            "tw-flex",
            "tw-gap-2",
            "tw-items-center",
            "tw-py-2",
            "tw-px-4",
            isDisabled ? "tw-cursor-default" : "tw-cursor-pointer",
            isDisabled && "tw-opacity-25",
            "tw-min-w-[10rem]",
            color === "error" ? "color-error" : "color-default",
            active && (color === "error" ? "bg-error" : "bg-primary-light"),
          )}
          onClick={(event) => {
            event.stopPropagation();
            close();
            if (!isDisabled) {
              if (isFunction(onSelect)) {
                onSelect();
              } else {
                dispatch(onSelect);
              }
            }
          }}
          data-tooltip={isTMessage(tooltip) || isTMessageParam(tooltip) ? tf(tooltip) : tooltip}
        >
          {renderMenuItemIcon(props)}
          <div className={"tw-grow"}>
            {isTMessage(label) || isTMessageParam(label) ? tf(label) : label}
          </div>
          {props.categoryTag && (
            <div className={classNames("tw-ml-7", "subtle")}>
              {isTMessage(props.categoryTag) || isTMessageParam(props.categoryTag)
                ? tf(props.categoryTag)
                : props.categoryTag}
            </div>
          )}
        </div>
      )}
    </Menu.Item>
  );
}

export type DropdownMenuCustomItemProps = {
  type: "custom-item";
  render: (props: { active: boolean; close: () => void }) => ReactElement;
  canSelect?: boolean;
  onSelect?: () => void;
};

function DropdownMenuCustomItem({
  render,
  canSelect = true,
  onSelect,
  close,
}: Omit<DropdownMenuCustomItemProps, "type"> & { close: () => void }) {
  return (
    <Menu.Item
      as={"div"}
      disabled={!canSelect}
      onClick={(event) => {
        event.stopPropagation();
        if (canSelect) {
          close();
          onSelect?.();
        }
      }}
      className={({ active }) => classNames("tw-cursor-pointer", active && "bg-primary-light")}
    >
      {({ active }) => render({ active, close })}
    </Menu.Item>
  );
}

export type DropdownMenuRadioItemProps = Omit<DropdownMenuItemProps, "type" | "icon"> & {
  type: "radio-item";
  isSelected?: boolean;
};

function DropdownMenuRadioItem({
  isSelected,
  ...props
}: { menuHasIcons: boolean } & Omit<DropdownMenuRadioItemProps, "type">) {
  return (
    <DropdownMenuItem {...props} icon={isSelected ? { name: "dot", weight: "fill" } : undefined} />
  );
}

export type DropdownMenuSeparatorProps = { type: "separator" };

function DropdownMenuSeparator() {
  return (
    <div className={"tw-py-1"}>
      <hr />
    </div>
  );
}

export type DropdownMenuLabelProps = {
  type: "label";
  label: TMessage | ReactNode;
};

function DropdownMenuLabel({
  label,
}: { menuHasIcons: boolean } & Omit<DropdownMenuLabelProps, "type">) {
  const { t } = useTranslation();
  return (
    <div
      className={classNames(
        "tw-flex",
        "tw-gap-2",
        "tw-items-center",
        "tw-pt-2",
        "tw-px-4",
        "tw-cursor-default",
        "tw-min-w-[10rem]",
      )}
    >
      <div className={"text-xs subtitle"}>{isTMessage(label) ? t(label) : label}</div>
    </div>
  );
}

export type DropdownMenuItemType =
  | DropdownMenuItemProps
  | DropdownMenuCustomItemProps
  | DropdownMenuRadioItemProps
  | DropdownMenuSeparatorProps
  | DropdownMenuLabelProps;

export type DropdownMenuItems = DropdownMenuItemType[];

function renderDropdownButton(
  button: DropdownMenuProps["button"],
  { ref, isOpen }: { ref: (el: Element | null) => void; isOpen: boolean },
) {
  switch (typeof button) {
    case "string":
      return <DropdownLinkButton ref={ref} isOpen={isOpen} label={button} />;
    case "function":
      return button({ ref, isOpen });
    default:
      switch (button.type) {
        case "dotdotdot":
          return (
            <DropdownIconButton
              ref={ref}
              icon={{ name: isOpen ? "dotdotdot" : "dotdotdot-vertical", weight: "bold" }}
              tooltip={button.tooltip ?? { key: "open-menu" }}
              {...button}
            />
          );
        case "icon":
          return <DropdownIconButton ref={ref} {...button} />;
        case "link":
          return <DropdownLinkButton ref={ref} isOpen={isOpen} {...button} />;
        case "button":
          return <DropdownButton ref={ref} isOpen={isOpen} {...button} />;
      }
  }
}

function renderDropdownItem(
  item: DropdownMenuItemType,
  index: number,
  menuHasIcons: boolean,
  close: () => void,
) {
  switch (item.type) {
    case "item":
      return <DropdownMenuItem key={index} {...item} menuHasIcons={menuHasIcons} />;
    case "custom-item":
      return <DropdownMenuCustomItem key={index} {...item} close={close} />;
    case "radio-item":
      return <DropdownMenuRadioItem key={index} {...item} menuHasIcons={menuHasIcons} />;
    case "label":
      return <DropdownMenuLabel key={index} {...item} menuHasIcons={menuHasIcons} />;
    case "separator":
      return <DropdownMenuSeparator key={index} {...item} />;
  }
}

export type DropdownMenuProps = {
  button:
    | string
    | ({ type: "dotdotdot"; tooltip?: string | TMessage } & Omit<
        IconButtonProps,
        "type" | "icon" | "tooltip"
      >)
    | ({ type: "icon"; tooltip?: string | TMessage } & Omit<IconButtonProps, "type" | "tooltip">)
    | ({ type: "link" } & Omit<LinkButtonProps, "type" | "arrow">)
    | ({ type: "button"; disableArrow?: boolean } & Omit<ButtonProps, "type" | "arrow">)
    | ((props: { ref: (el: Element | null) => void; isOpen: boolean }) => ReactElement);
  items: Array<DropdownMenuItemType | null | undefined | false | "" | 0>;
} & FloatingDropdownParams;

export function DropdownMenu({ button, items, ...props }: DropdownMenuProps) {
  const { refs, floatingStyles } = useFloatingDropdown(props);
  const hasIcons = items.some(
    (item) => item && (item.type === "radio-item" || (item.type === "item" && item.icon)),
  );

  return (
    <Menu>
      {({ open, close }) => (
        <>
          <Menu.Button as={Fragment} disabled={props.disabled}>
            {renderDropdownButton(button, { ref: refs.setReference, isOpen: open })}
          </Menu.Button>
          {open && (
            <FloatingOverlay layer={"dropdown"} modal>
              <Menu.Items
                ref={refs.setFloating}
                className={classNames(
                  "tw-rounded",
                  "tw-border",
                  "border-gray",
                  "tw-shadow",
                  "bg-default",
                  "tw-inline-flex",
                  "tw-flex-col",
                  "tw-py-1",
                  "tw-overflow-auto",
                )}
                style={{ ...floatingStyles, width: "max-content" }}
                static
              >
                {items.map((item, index) =>
                  item ? renderDropdownItem(item, index, hasIcons, close) : undefined,
                )}
              </Menu.Items>
            </FloatingOverlay>
          )}
        </>
      )}
    </Menu>
  );
}
